// Selectable candidate cards for decision-support workflows — replaces a blank
// "pick something" text field with evidence the user can actually compare.

import {FC} from "react";
import type {DecisionCandidate} from "./decisionSupport";

interface DecisionCandidateCardsProps {
    candidates: DecisionCandidate[] | null;
    selectedId: string | null;
    onSelect: (candidateId: string) => void;
    justUpdatedByChat?: boolean;
}

export const DecisionCandidateCards: FC<DecisionCandidateCardsProps> = ({
    candidates,
    selectedId,
    onSelect,
    justUpdatedByChat
}) => {
    if (candidates === null) {
        return (
            <div className="pi-candidate-skeleton" aria-label="Loading candidates">
                <div className="pi-candidate-skeleton__row" />
                <div className="pi-candidate-skeleton__row" />
                <div className="pi-candidate-skeleton__row" />
            </div>
        );
    }

    return (
        <div className="pi-candidate-cards" role="radiogroup">
            {candidates.map((candidate) => {
                const selected = candidate.id === selectedId;
                return (
                    <button
                        key={candidate.id}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        className={`pi-candidate-card${
                            selected ? " pi-candidate-card--selected" : ""
                        }${
                            selected && justUpdatedByChat
                                ? " pi-candidate-card--updated"
                                : ""
                        }`}
                        onClick={() => onSelect(candidate.id)}
                    >
                        <div className="pi-candidate-card__top">
                            <span className="pi-candidate-card__name">
                                {candidate.name}
                            </span>
                            {candidate.recommended && (
                                <span className="pi-candidate-card__badge">
                                    AI recommended
                                </span>
                            )}
                        </div>
                        <p className="pi-candidate-card__why">{candidate.whyLine}</p>
                        <div className="pi-candidate-card__facts">
                            {candidate.facts.map((fact) => (
                                <div
                                    key={fact.label}
                                    className="pi-candidate-card__fact"
                                >
                                    <span className="pi-candidate-card__fact-label">
                                        {fact.label}
                                    </span>
                                    <span className="pi-candidate-card__fact-value">
                                        {fact.value}
                                    </span>
                                </div>
                            ))}
                        </div>
                        {candidate.segments && candidate.segments.length > 0 && (
                            <div className="pi-candidate-card__segments">
                                {candidate.segments.map((segment) => (
                                    <span
                                        key={segment}
                                        className="pi-candidate-card__segment-chip"
                                    >
                                        {segment}
                                    </span>
                                ))}
                            </div>
                        )}
                    </button>
                );
            })}
        </div>
    );
};

export default DecisionCandidateCards;
